import "../../css/territory.css";
import { useState } from "react";
import { useParams } from "react-router";
import { Map as MaplibreMap } from "maplibre-gl";
import { Badge, Container, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useSharedTerritory } from "../../api/territory";
import QueryKeys from "../../api/queryKeys";
import { addressesForPDF, mapFontStack, PrintOptions, terrLabel } from "../congregation/territory/common";
import { MapWithGeoJson } from "../congregation/territory/maps";
import { TerritoryPrintOptionsMenu } from "../congregation/territory/printOptionsMenu";
import { TagList } from "../congregation/tags";
import { singleTerritoryPDF } from "../congregation/territory/pdf";
import { MapGenerator, MapKind } from "../../@adapters/map-export/mapglExport";
import { HGBugsnagNotify } from "../../helpers/bugsnag";
import { territoryAddressCompare } from "../../helpers/address";
import { PDFOrientation } from "../../types/paper";
import { TerritoryAddress } from "../../types/scheduling/territory";
import { Cong } from "../../types/cong";
import { useWindowSizeString } from "../../helpers/useWindowSize";
import { SuspenseFallback } from "../utils";

function sharedAddressLine(addr: TerritoryAddress): string {
  return [addr.line1, addr.line2, addr.city, addr.state, addr.postalcode].filter(Boolean).join(", ");
}

export default function SharedTerritory() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [shareToken] = useState(() => token ?? "");
  const [mapLibreMap, setMapLibreMap] = useState<MaplibreMap>();
  const [showPrintSpinner, setShowPrintSpinner] = useState(false);
  const [printDropdownShow, setPrintDropdownShow] = useState(false);
  const [printOptions, setPrintOptions] = useState<PrintOptions>({
    excludeHideOnMapAddresses: false,
    excludeAllAddresses: false,
    dncOnly: false,
    invertedShading: false,
    mapFullPage: false,
  });
  const windowSize = useWindowSizeString();
  const queryClient = useQueryClient();
  const query = useSharedTerritory(shareToken);

  // Seed the tags cache from the share payload so any component calling useTags()
  // (e.g. AddressesTable's filter UI) finds data without hitting the auth API.
  // staleTime: Infinity prevents a refetch that would trigger signOut on 401.
  if (query.isSuccess && query.data) {
    queryClient.setQueryDefaults([QueryKeys.Tags], { staleTime: Infinity });
    queryClient.setQueryData([QueryKeys.Tags], query.data.tags);
  }

  if (query.isLoading) return <SuspenseFallback />;

  if (query.isError) {
    return (
      <Container className="mt-5 text-center">
        <h5>{t("import.stats.invalid")}</h5>
      </Container>
    );
  }

  if (!query.data) return null;

  const { territory, settings, cong } = query.data;

  const boundaries = territory.boundaries ? JSON.parse(territory.boundaries) : undefined;
  const annotations = territory.annotations ? JSON.parse(territory.annotations) : undefined;
  const mapNotReady = !!boundaries && !mapLibreMap;
  const addresses = territory.addresses?.sort(territoryAddressCompare)?.map((a, i) => ({ ...a, pinLabel: `${i + 1}` }));

  const printTerritory = async (orientation: PDFOrientation) => {
    if (!mapLibreMap) return;
    setShowPrintSpinner(true);
    const pdfAddrs = addressesForPDF(territory.addresses ?? [], printOptions).sort(territoryAddressCompare);
    try {
      const mapGen = new MapGenerator(
        mapLibreMap,
        boundaries,
        annotations,
        pdfAddrs.filter((a) => !a.hideOnMap),
        mapFontStack(settings),
        orientation,
        printOptions.invertedShading,
        printOptions.mapFullPage,
      );
      const printImageBlob = await mapGen.generate();
      await singleTerritoryPDF(
        territory,
        pdfAddrs,
        cong as Cong,
        settings,
        printImageBlob,
        orientation,
        printOptions.mapFullPage,
        MapKind.single,
        undefined,
        [],
      );
    } catch (err: any) {
      console.error("shared territory print", err);
      HGBugsnagNotify("sharedTerritoryPrint", err);
    } finally {
      setShowPrintSpinner(false);
    }
  };

  return (
    <Container className="mt-3">
      <div className="d-flex justify-content-between">
        <div>
          <h4>{terrLabel(territory)}</h4>
          <p className="whitespace-pre">{territory.notes}</p>
        </div>
        <TagList tags={territory.tags ?? []} />
        <TerritoryPrintOptionsMenu
          show={printDropdownShow}
          showSpinner={showPrintSpinner}
          setShow={setPrintDropdownShow}
          printOptions={printOptions}
          setPrintOptions={setPrintOptions}
          printTerritory={printTerritory}
          mapNotReady={mapNotReady}
          className=""
        />
      </div>

      {(!!boundaries || !!annotations) && (
        <div className="mt-3">
          <MapWithGeoJson
            key={`${territory.id}_${windowSize}`}
            addresses={addresses}
            settings={settings}
            geojson={boundaries}
            setMaplibreMap={setMapLibreMap}
            territory={territory}
            navigateToMap={() => {}}
            pubMode
            fullHeight
          />
        </div>
      )}

      {!!addresses?.length && <SharedAddressesTable addresses={addresses} />}
    </Container>
  );
}

function SharedAddressesTable(props: { addresses: TerritoryAddress[] }) {
  const { t } = useTranslation();
  return (
    <Table size="sm" className="mt-3" responsive>
      <thead>
        <tr>
          <th>#</th>
          <th>{t("userinfo.address.0")}</th>
          <th>{t("general.name")}</th>
          <th>{t("general.notes")}</th>
        </tr>
      </thead>
      <tbody>
        {props.addresses.map((addr) => (
          <tr key={addr.id}>
            <td className="text-muted">{addr.pinLabel}</td>
            <td>
              {sharedAddressLine(addr)}
              {addr.dnc && (
                <Badge bg="danger" className="ms-2">
                  {t("schedules.territory.do-not-call")}
                </Badge>
              )}
              {!!addr.tags?.length && (
                <TagList tags={addr.tags.filter((t) => !t.global && t.name === "dnc")} mini className="ms-1" />
              )}
            </td>
            <td>{addr.name}</td>
            <td>{addr.notes}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
